import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CategoryCard } from '../components/courses';
import { getCategories, getCourses } from '../services/api';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.error};
  font-size: 1.1rem;
`;

const CoursesPage = () => {
  const [categories, setCategories] = useState([]);
  const [courseCounts, setCourseCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch categories
        const categoriesData = await getCategories();
        setCategories(categoriesData);

        // Fetch all courses to get counts (no pagination)
        const coursesData = await getCourses({ limit: 1000 });
        
        const counts = {};
        
        categoriesData.forEach(category => {
          const categoryCourses = coursesData.courses ? coursesData.courses.filter(course => 
            course.category._id === category._id
          ) : [];
          counts[category._id] = categoryCourses.length;
        });
        
        setCourseCounts(counts);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Container>
        <Title>Our Courses</Title>
        <Subtitle>Browse our comprehensive range of nationally recognized qualifications.</Subtitle>
        <LoadingContainer>
          Loading categories...
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Title>Our Courses</Title>
        <Subtitle>Browse our comprehensive range of nationally recognized qualifications.</Subtitle>
        <ErrorContainer>
          {error}
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Our Courses</Title>
      <Subtitle>
        Browse our comprehensive range of nationally recognized qualifications across various industries. 
        Click on any category to explore the courses available in that field.
      </Subtitle>
      
      <CategoriesGrid>
        {categories.map((category) => (
          <CategoryCard
            key={category._id}
            category={category}
            courseCount={courseCounts[category._id] || 0}
          />
        ))}
      </CategoriesGrid>
    </Container>
  );
};

export default CoursesPage; 